import { inject } from '@angular/core';
import { Route, Router, Routes } from '@angular/router';
import { EDI_PROTOCOLS_URL, EVENT_STREAMING_URL, MESSAGE_BROKER_URL } from '@conecta/ui/submodule-nav/submodules';

/**
 * Enlace antiguo de una funcionalidad que era un módulo propio del catálogo (`/integraciones/<funcionalidad>/**`): redirige a la
 * misma pantalla dentro de su submódulo, con sus parámetros.
 */
function legacy(path: string, submoduleUrl: string): Route {
  return {
    path,
    children: [{ path: '**', redirectTo: ({ url, queryParams }) => inject(Router).createUrlTree([submoduleUrl, path, ...url.map(s => s.path)], { queryParams }) }],
  };
}

/**
 * Rutas de los módulos Conecta nuevos, montadas bajo /integraciones.
 * Las rutas Conecta anteriores siguen declaradas en app.routes.ts (no se mueven).
 */
export const CONECTA_ROUTES: Routes = [
  { path: 'transformaciones', loadChildren: () => import('@conecta/feature-transformation/transformation.routes').then(m => m.TRANSFORMATION_ROUTES) },
  { path: 'procesamiento-datos', loadChildren: () => import('@conecta/feature-transformation/processing/processing.routes').then(m => m.PROCESSING_ROUTES) },
  // M03: Ejecuciones y Streaming dejaron de ser módulos del catálogo; sus enlaces antiguos redirigen a su funcionalidad.
  { path: 'ejecuciones-transformacion', loadChildren: () => import('@conecta/feature-transformation/transformation.routes').then(m => m.LEGACY_EXECUTIONS_ROUTES) },
  { path: 'streaming', loadChildren: () => import('@conecta/feature-transformation/streaming/streaming.routes').then(m => m.LEGACY_STREAMING_ROUTES) },

  // ════════ M06 Mensajería y Arquitectura Event-Driven ════════
  // 6.1 Message Broker Nativo: Colas y topics, Guaranteed delivery, Dead Letter Queue (DLQ), Message routing inteligente y Transacciones XA.
  {
    path: 'message-broker',
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'colas-topics' },
      { path: 'colas-topics', loadChildren: () => import('@conecta/feature-messaging/colas-topics/colas-topics.routes').then(m => m.CHANNELS_ROUTES) },
      { path: 'entrega-garantizada', loadChildren: () => import('@conecta/feature-messaging/entrega-garantizada/entrega-garantizada.routes').then(m => m.DELIVERY_ROUTES) },
      { path: 'dead-letter-queue', loadChildren: () => import('@conecta/feature-messaging/dead-letter-queue/dead-letter-queue.routes').then(m => m.DEAD_LETTER_ROUTES) },
      { path: 'enrutamiento-mensajes', loadChildren: () => import('@conecta/feature-messaging/enrutamiento-mensajes/enrutamiento-mensajes.routes').then(m => m.ROUTING_ROUTES) },
      { path: 'transacciones-xa', loadChildren: () => import('@conecta/feature-messaging/transacciones-xa/transacciones-xa.routes').then(m => m.XA_ROUTES) },
    ],
  },
  // 6.2 Event Streaming (Kafka-native): Kafka producer/consumer, Change Data Capture (CDC), Event replay, Schema Registry y Stream processing.
  {
    path: 'event-streaming-kafka',
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'topics-kafka' },
      { path: 'topics-kafka', loadChildren: () => import('@conecta/feature-messaging/topics-kafka/topics-kafka.routes').then(m => m.KAFKA_ROUTES) },
      { path: 'change-data-capture', loadChildren: () => import('@conecta/feature-messaging/change-data-capture/change-data-capture.routes').then(m => m.CDC_ROUTES) },
      { path: 'event-replay', loadChildren: () => import('@conecta/feature-messaging/event-replay/event-replay.routes').then(m => m.REPLAY_ROUTES) },
      { path: 'schema-registry', loadChildren: () => import('@conecta/feature-messaging/schema-registry/schema-registry.routes').then(m => m.SCHEMAS_ROUTES) },
      { path: 'stream-processing', loadChildren: () => import('@conecta/feature-messaging/stream-processing/stream-processing.routes').then(m => m.STREAMS_ROUTES) },
    ],
  },
  ...['colas-topics', 'entrega-garantizada', 'dead-letter-queue', 'enrutamiento-mensajes', 'transacciones-xa'].map(path => legacy(path, MESSAGE_BROKER_URL)),
  ...['topics-kafka', 'change-data-capture', 'event-replay', 'schema-registry', 'stream-processing'].map(path => legacy(path, EVENT_STREAMING_URL)),

  // ════════ DevOps y CI/CD ════════
  { path: 'despliegue-continuo', loadChildren: () => import('@conecta/feature-devops-deployment/deployment.routes').then(m => m.DEPLOYMENT_ROUTES) },

  // ════════ M12 Integración B2B y Socios Comerciales ════════
  // 12.1 EDI y Protocolos B2B: EDI X12 / EDIFACT nativo, Transporte AS2 / SFTP, Onboarding de socios y Rastreo de documentos.
  {
    path: 'edi-protocolos-b2b',
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'edi-x12-edifact' },
      { path: 'edi-x12-edifact', loadChildren: () => import('@conecta/feature-b2b/edi-x12-edifact/edi-x12-edifact.routes').then(m => m.EDI_ROUTES) },
      { path: 'transporte-as2-sftp', loadChildren: () => import('@conecta/feature-b2b/transporte-as2-sftp/transporte-as2-sftp.routes').then(m => m.TRANSPORT_ROUTES) },
      { path: 'onboarding-socios', loadChildren: () => import('@conecta/feature-b2b/onboarding-socios/onboarding-socios.routes').then(m => m.ONBOARDING_ROUTES) },
      { path: 'rastreo-documentos', loadChildren: () => import('@conecta/feature-b2b/rastreo-documentos/rastreo-documentos.routes').then(m => m.TRACKING_ROUTES) },
    ],
  },
  ...['edi-x12-edifact', 'transporte-as2-sftp', 'onboarding-socios', 'rastreo-documentos'].map(path => legacy(path, EDI_PROTOCOLS_URL)),
  // Portal self-service del socio externo: módulo y permiso propios (12.1.3 RF-021).
  { path: 'portal-socios', loadChildren: () => import('@conecta/feature-b2b/portal-socios/portal-socios.routes').then(m => m.PORTAL_ROUTES) },

  // ════════ M14 Asistente de IA e Inteligencia del Sistema ════════
  // 14.1 IA Generativa y Asistente: Generación de flujos con IA, Documentación automática, Detección de anti-patrones y
  // Chat de soporte contextual (el Asistente de DataWeave/código, 14.1.2, ya está cubierto por el IDE avanzado Pro Code).
  { path: 'ia-generativa-asistente', loadChildren: () => import('@conecta/feature-assistant/assistant.routes').then(m => m.ASSISTANT_ROUTES) },
];
